import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnInit, inject, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { CBox_PublicCreateLockerLowActivityReportDataParams, CBox_PublicCreateReportResponse } from "@server/services/cbox/public/api/v1/resources/reports/types";
import { TreeNode } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { CheckboxModule } from "primeng/checkbox";
import { InputNumberModule } from "primeng/inputnumber";
import { MultiSelectModule } from "primeng/multiselect";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TreeSelectModule } from "primeng/treeselect";
import { markFormDirty } from "src/helpers/functions";
import { ApiService, handlePublicApiError } from "src/services/api/api.service";
import { ReportsService } from "src/services/reports/reports.service";
import { ToastService } from "src/services/toast/toast.service";
import { ZonesService } from "src/services/zones/zones.service";
import {
  buildZoneTree,
  cloneZoneTree,
  zoneSelectionLabel,
  zoneTreeOrder,
} from "../../../zone-tree";

type LowActivityReportDialogData = {
  zones?: number[];
  zoneNodes?: TreeNode[];
  interval?: string[] | Date[];
  thresholdPercent?: number;
  activityTypes?: string[];
  includeAll?: boolean;
};

@Component({
  selector: "app-cbox-profile-locker-low-activity-report-generate-dialog",
  templateUrl:
    "./cbox-profile-locker-low-activity-report-generate-dialog.component.html",
  styleUrls: [
    "./cbox-profile-locker-low-activity-report-generate-dialog.component.scss",
  ],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    CalendarModule,
    ButtonModule,
    CheckboxModule,
    InputNumberModule,
    MultiSelectModule,
    TreeSelectModule,
    ProgressSpinnerModule,
  ],
})
export class CBoxProfileLockerLowActivityReportGenerateDialogComponent
implements OnInit {

  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private reportsService = inject(ReportsService);
  private zonesService = inject(ZonesService);
  private dialogRef = inject(
    MatDialogRef<CBoxProfileLockerLowActivityReportGenerateDialogComponent>);
  data = inject<LowActivityReportDialogData>(MAT_DIALOG_DATA);

  readonly activityTypeOptions = [
    { label: "Depunere agent", value: "PICK_UP" },
    { label: "Depunere utilizator", value: "DROP_OFF" },
  ];

  reportForm: FormGroup;
  zoneNodes = signal<TreeNode[]>([]);
  loadingZones = signal<boolean>(true);
  generating = signal<boolean>(false);

  constructor() {
    const oneMonthAgo = new Date();

    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    oneMonthAgo.setHours(0, 0, 0, 0);
    const today = new Date();

    today.setHours(23, 59, 59, 999);
    this.reportForm = this.fb.group({
      startDate: [oneMonthAgo, Validators.required],
      endDate: [today, Validators.required],
      type: [["PDF", "XLSX"], Validators.required],
      zones: [[], Validators.required],
      thresholdPercent: [this.data.thresholdPercent ?? 25, Validators.required],
      // ?? (not a length check): an explicitly cleared selection in the
      // list must stay empty here and be caught by Validators.required.
      activityTypes: [
        this.data.activityTypes ?? ["PICK_UP", "DROP_OFF"],
        Validators.required,
      ],
      includeAll: [this.data.includeAll ?? false],
    });

    if (this.data.interval && this.data.interval.length === 2) {
      const start = new Date(this.data.interval[0]);

      start.setHours(0, 0, 0, 0);
      const end = new Date(this.data.interval[1]);

      end.setHours(23, 59, 59, 999);
      this.reportForm.patchValue({ startDate: start, endDate: end });
    }
  }

  ngOnInit(): void {
    this.loadZones();
  }

  zoneSelectionLabel(selected: TreeNode[] | undefined): string {
    return zoneSelectionLabel(selected, this.loadingZones());
  }

  generateReport(): void {
    if (this.reportForm.invalid) {
      markFormDirty(this.reportForm);

      return;
    }
    this.generating.set(true);
    this.api.post<CBox_PublicSuccessResponse<CBox_PublicCreateReportResponse>>(
      "backend/reports/lockers/low-activity", this.getCreateStructure())
      .subscribe({
        next: response => {
          this.toastService.showSuccessToast(
            "Confirmare", "Raportul dumneavoastră este în curs de generare");
          this.reportsService.addReportToActiveList(response.data);
          this.generating.set(false);
          this.dialogRef.close();
        },
        error: (e: HttpErrorResponse) => {
          handlePublicApiError(e, this.toastService);
          this.generating.set(false);
        },
      });
  }

  private async loadZones(): Promise<void> {
    // The opening list page already has the tree loaded; reuse it so the
    // preselected zones show instantly instead of after a re-fetch. Cloned,
    // so this tree-select never shares mutable node state with the list's.
    if (this.data.zoneNodes?.length) {
      const nodes = cloneZoneTree(this.data.zoneNodes);

      this.zoneNodes.set(nodes);
      this.preselectZones(nodes);
      this.loadingZones.set(false);

      return;
    }
    this.loadingZones.set(true);
    try {
      const zones = await this.zonesService.getList();
      const nodes = buildZoneTree(zones);

      this.zoneNodes.set(nodes);
      this.preselectZones(nodes);
    } catch {
      this.zoneNodes.set([]);
    } finally {
      this.loadingZones.set(false);
    }
  }

  private preselectZones(roots: TreeNode[]): void {
    const wanted = new Set(this.data.zones ?? []);

    if (wanted.size === 0) {
      // Nothing selected in the list filter: default to the root zones,
      // which roll up their whole subtree server-side.
      this.reportForm.patchValue({ zones: [...roots] });

      return;
    }
    const selected: TreeNode[] = [];
    const walk = (nodes: TreeNode[]): void => {
      for (const node of nodes) {
        if (wanted.has(node.data as number)) {
          selected.push(node);
        }
        if (node.children) {
          walk(node.children);
        }
      }
    };

    walk(roots);
    this.reportForm.patchValue({ zones: selected });
  }

  private getCreateStructure():
  CBox_PublicCreateLockerLowActivityReportDataParams {
    const selected =
      (this.reportForm.get("zones")?.value ?? []) as TreeNode[];
    const order = zoneTreeOrder(this.zoneNodes());
    const zones = selected
      .map(node => node.data as number)
      .filter(id => typeof id === "number")
      .sort((a, b) => (order.get(a) ?? Infinity) - (order.get(b) ?? Infinity));

    return {
      startDate: this.reportForm.get("startDate")?.value,
      endDate: this.reportForm.get("endDate")?.value,
      fileFormats: this.reportForm.get("type")?.value,
      thresholdPercent: this.reportForm.get("thresholdPercent")?.value,
      activityTypes: this.reportForm.get("activityTypes")?.value,
      includeAll: this.reportForm.get("includeAll")?.value,
      zones,
    };
  }
}
